import { Suspense, lazy } from 'react';
import { useParams, useNavigate, Outlet } from 'react-router-dom';
import MenuMedia from './MenuMedia';
// Import directo (no el barrel `../features/map`) -- ese barrel también re-exporta `Map` de
// forma estática, y Rollup arrastra el módulo completo (con `mediasoup-client`) al bundle
// principal apenas algo importa el barrel, aunque `Map` en sí se cargue vía `lazy()` más abajo.
import MapTools from '../features/map/components/MapTools';
import SettingsAssets from '../features/map/components/SettingsAssets';
import ActivityTracker from './ActivityTracker';
import './AppShell.css';

import { useSessionStatus, useCurrentUser, useAuthUser } from '../context/authHooks';
import { useToast } from '../shared/ui/Toast/ToastProvider';
import { useShellStore } from '../shared/store/useShellStore';
import { useScreenRecordStore } from '../shared/media/useScreenRecordStore';
import { useAppShellRoute } from './useAppShellRoute';
import { useMenuWidth } from './useMenuWidth';

// Fase 43: `Map` importa `mediasoup-client` (~150-250kB) de forma estática -- se carga vía
// `lazy()` para que ese motor WebRTC no entre al bundle inicial de cualquier usuario. Import
// directo del archivo (no el barrel de `features/map`, que reexporta `MapTools`) para que el
// `import()` apunte a un chunk propio.
const Map = lazy(() => import('../features/map/components/Map'));
const DappViewer = lazy(() => import('../features/settings/components/dapps-panel/DappViewer'));

// Helper: ISO week number (bottom status bar).
function getISOWeek(d) {
    d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

// frontend-restructuring §1b — `AppShell` is the persistent chrome of the authenticated
// `/:spaceSlug` subtree: the map background, the top media bar, the map tools, the bottom
// status bar, the dapp viewer overlay. Everything route-derived (the left rail, `Menu2`, the
// float-over-map panels, the standalone task/event overlay) is `SpaceScreen`, rendered through
// `<Outlet/>`. `AppShell` is a layout route (`App.jsx`), so it stays mounted across every
// in-space navigation and `Map` / `Menu2` never remount.
const AppShell = () => {
    const { loading, isDbConnected } = useSessionStatus();
    // `ActivityTracker` recibe `user.id` (alcance angosto de `useCurrentUser()`). Fase 5.2:
    // movido a `layout/` — es shell chrome (side-effects app-wide, `return null`), no un
    // componente de feature, así que sus listeners de `window` no violan la frontera
    // presentacional.
    const { id: userId } = useCurrentUser();
    // `Map` (montado abajo) es presentacional y lee campos anchos de `user`
    // (`map_pos_x`/`avatar_char_id`/`image`/`uid`/…) -- 04-enforce.md 4.4.
    const mapUser = useAuthUser();
    const toast = useToast();
    const { spaceSlug } = useParams();
    const navigate = useNavigate();

    const route = useAppShellRoute();
    const {
        logicalPathname, activeDapp,
        isDappView, isDocs, isCalendar, isUserProfile, isDatabase, isTask,
    } = route;
    const showMapTools = logicalPathname.startsWith('/maptools');

    const menuWidthInfo = useMenuWidth({ isDocs, isCalendar, isUserProfile, isDatabase, isDappView, isTask });
    const { menuWidth } = menuWidthInfo;

    // Chrome-only store reads (the panel dispatch reads its own slice inside `SpaceScreen`).
    const drawingColor = useShellStore((s) => s.drawingColor);
    const setDrawingColor = useShellStore((s) => s.setDrawingColor);
    const isMapLocked = useShellStore((s) => s.isMapLocked);
    const setIsMapLocked = useShellStore((s) => s.setIsMapLocked);
    const toggleFullScreenStream = useShellStore((s) => s.toggleFullScreenStream);
    const isBuzzing = useShellStore((s) => s.isBuzzing);
    const buzz = useShellStore((s) => s.buzz);
    const openAiPanel = useShellStore((s) => s.openAiPanel);
    const lastRecordingUrl = useScreenRecordStore((s) => s.lastUrl);

    // The child element (`ConnectedSpace`) decides Login-in-place vs bounce for a
    // not-yet-connected user — render only the `<Outlet/>`, no chrome, until it does.
    if (loading) return <div className="p-10 text-white">Loading...</div>;
    if (!isDbConnected) return <Outlet context={{ route, ...menuWidthInfo }} />;

    return (
        <div className="home-container" style={{ '--control-menu-width': menuWidth }}>
            <ActivityTracker userId={userId} />
            <MenuMedia isBuzzing={isBuzzing} onAiToggle={openAiPanel} />
            {showMapTools && (
                <MapTools
                    drawingColor={drawingColor}
                    setDrawingColor={setDrawingColor}
                    isMapLocked={isMapLocked}
                    setIsMapLocked={setIsMapLocked}
                    onClose={() => navigate(`/${spaceSlug || ''}`)}
                />
            )}
            {/* SettingsAssets maneja su propio render condicional (isOpen). */}
            <SettingsAssets />

            {/* Per-family screen: `.control-menu1` + `Menu2` + float-over-map panels + standalone overlay. */}
            <Outlet context={{ route, ...menuWidthInfo }} />

            <div className="bottom-status-bar">
                <div className="week-indicator">week {getISOWeek(new Date())}</div>
                <div className="week-indicator" style={{ marginLeft: '10px', fontSize: '11px', opacity: 0.7 }}>
                    {typeof GIT_VERSION !== 'undefined' ? `commits: ${Number(GIT_VERSION.count).toLocaleString()} • id: ${GIT_VERSION.commit.slice(-3)}` : 'dev'}
                </div>
                {lastRecordingUrl && (
                    <a href={lastRecordingUrl} target="_blank" rel="noopener noreferrer" className="recording-tag">
                        New Recording
                    </a>
                )}
            </div>

            {/* Map Layer — persistent background chrome, lazy so `mediasoup-client` stays out of the main chunk. */}
            <div className="map-wrapper">
                <Suspense fallback={null}>
                    <Map
                        user={mapUser}
                        spaceSlug={spaceSlug}
                        drawingColor={drawingColor}
                        isMapLocked={isMapLocked}
                        onFullscreenToggle={toggleFullScreenStream}
                        onReceiveBuzz={buzz}
                    />
                </Suspense>
            </div>

            {/* DappViewer overlay panel — covers Menu1 from the left edge. */}
            <Suspense fallback={null}>
                <DappViewer activeDapp={activeDapp} onClose={() => navigate(route.prefix + (route.effectivePath === '/' ? '' : route.effectivePath))} user={mapUser} toast={toast} />
            </Suspense>
        </div>
    );
};

export default AppShell;
